import { useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDebouncedCallback } from 'use-debounce';
import { Dialog } from '@components/dialog';
import { FormControlLabel } from '@components/formControlLabel';
import { RadioButton } from '@components/radioButton';
import { TypeAheadFilter } from '@components/table/filters/TypeAheadFilter';
import { ToastType } from '@components/toast/ToastType.enum';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { handleScrollToBottom } from '@utils/HandleScrollToBottom';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { useQuote } from '@services/quotes';
import {
  ReassignCustomerContactChoice,
  ReassignCustomerContactField,
  ReassignCustomerPreviewResponseWithAccess,
  useReassignQuote,
  useReassignQuotePreview,
} from '@services/quotes';
import { useSearchPaginatedCustomers } from '@services/customer';

interface Props {
  quoteId: string;
  handleClose: () => void;
}

interface CustomerOption {
  /** Customer id (the typeahead's option `value`). */
  value: string;
  label: string;
}

type View = 'select' | 'reconcile';

const CONTACT_FIELDS: ReassignCustomerContactField[] = ['phone', 'email', 'mailingAddress', 'billingAddress'];

export const ReassignQuoteDialog = ({ quoteId, handleClose }: Props) => {
  const { quote } = useQuote(quoteId);
  const { enqueueSnackbar } = useSnackbar();
  const { previewReassignQuote, isPending: isPreviewing } = useReassignQuotePreview();
  const { reassignQuote, isPending: isReassigning } = useReassignQuote();

  const [view, setView] = useState<View>('select');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [preview, setPreview] = useState<ReassignCustomerPreviewResponseWithAccess | null>(null);
  const [contactChoices, setContactChoices] = useState<
    Partial<Record<ReassignCustomerContactField, ReassignCustomerContactChoice>>
  >({});
  const [borAcknowledged, setBorAcknowledged] = useState(false);
  // All-or-none decision for existing customer-user access. `null` until the operator
  // chooses, so we can require an explicit answer before allowing the reassignment.
  const [keepAccess, setKeepAccess] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { customers, fetchNextPage, isLoading, isFetching, isFetchingNextPage, isPending } =
    useSearchPaginatedCustomers({ name: searchTerm, pageLimit: 25, sort: 'name' });

  const handleDebouncedSearch = useDebouncedCallback(
    (value: string) => setSearchTerm(value),
    DEBOUNCE_DELAYS.FORM_TYPING,
  );

  // Exclude the customer the quote is already assigned to from the picker.
  const options: CustomerOption[] = useMemo(
    () => customers.filter((c) => c.id !== quote?.customer?.id).map((c) => ({ value: c.id, label: c.name })),
    [customers, quote?.customer?.id],
  );

  const isSearching = isLoading || isFetching || isFetchingNextPage || isPending;

  // Reset everything downstream of the customer pick so a stale target can never be
  // carried forward (used when the typed text diverges from the selection and on Back).
  const clearSelection = () => {
    setSelectedCustomer(null);
    setPreview(null);
    setContactChoices({});
    setBorAcknowledged(false);
    setKeepAccess(null);
  };

  const handleInputChange = (value: string) => {
    // If the operator edits the search text away from the chosen option, drop the stale
    // selection immediately so Continue can't proceed with a customer they're no longer
    // looking at.
    setSelectedCustomer((prev) => (prev && prev.label !== value ? null : prev));
    handleDebouncedSearch(value);
  };

  const handleContinue = async () => {
    if (!selectedCustomer) return;
    setErrorMessage(null);
    try {
      const result = await previewReassignQuote({ quoteId, targetCustomerId: selectedCustomer.value });
      setPreview(result);
      // Default every field to "keep"; the operator opts into updates explicitly.
      setContactChoices(Object.fromEntries(CONTACT_FIELDS.map((f) => [f, 'keep'])));
      setBorAcknowledged(false);
      setKeepAccess(null);
      setView('reconcile');
    } catch (error) {
      // Surface the API's specific ineligibility reason (already bound, in-progress
      // binding, missing Named Insured, inactive target, …) rather than a generic line.
      setErrorMessage(await getApiErrorMessage(error, 'Unable to reassign this quote to the selected customer.'));
    }
  };

  const handleBack = () => {
    // The single-select typeahead remounts blank on the way back, so clear the selection
    // too — otherwise Continue would stay enabled against an invisible stale target.
    clearSelection();
    setErrorMessage(null);
    setView('select');
  };

  const handleSubmit = async () => {
    if (!preview) return;
    setErrorMessage(null);
    const needsAccessDecision = preview.access.customerUsers.length > 0;
    try {
      await reassignQuote({
        quoteId,
        targetCustomerId: preview.targetCustomerId,
        contactChoices,
        borConflictAcknowledged: preview.borConflict.hasConflict ? borAcknowledged : undefined,
        keepCustomerUserAccess: needsAccessDecision ? (keepAccess ?? false) : undefined,
      });
      enqueueSnackbar('Quote reassigned to the selected customer', { variant: ToastType.Success });
      // The quote's customer, named insured, and status all changed; the mutation's
      // onSuccess invalidates the quote/quote-item/quote-form queries so the page
      // refreshes its data. (No full reload — the invalidations are the refresh mechanism.)
      handleClose();
    } catch (error) {
      // The move can fail either before commit (eligibility/BOR) or AFTER it has
      // committed (post-move re-rate/underwriting) — the API returns distinct messages
      // for each, so surface the real one instead of a misleading "try again".
      setErrorMessage(await getApiErrorMessage(error, 'Failed to reassign the quote.'));
    }
  };

  const setChoice = (field: ReassignCustomerContactField, choice: ReassignCustomerContactChoice) =>
    setContactChoices((prev) => ({ ...prev, [field]: choice }));

  // ----- Select target customer -----
  if (view === 'select') {
    return (
      <Dialog
        open
        title='Reassign Customer'
        size='large'
        primaryBtnText='Continue'
        primaryBtnAction={handleContinue}
        isPrimaryBtnDisabled={!selectedCustomer || isPreviewing}
        isPrimaryBtnLoading={isPreviewing}
        secondaryBtnText='Cancel'
        secondaryBtnAction={handleClose}
        handleCloseDialog={handleClose}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', px: 3, mt: 1, mb: 2, gap: 1.5 }}>
          <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 400 }}>
            Move this unbound New Business quote to a different active customer. Search for the target customer below.
          </Typography>
          <TypeAheadFilter
            label='Target customer'
            options={options}
            loading={isSearching}
            value={selectedCustomer}
            onChange={(value: CustomerOption | undefined) => setSelectedCustomer(value ?? null)}
            onInputChange={(_e, value) => handleInputChange(value)}
            slotProps={{
              listbox: {
                onScroll: (e) => handleScrollToBottom(e, fetchNextPage),
                style: { maxHeight: '300px' },
              },
            }}
          />
          {errorMessage && (
            <Alert severity='error'>
              <AlertTitle>Cannot proceed</AlertTitle>
              {errorMessage}
            </Alert>
          )}
        </Box>
      </Dialog>
    );
  }

  // ----- Reconcile + confirm -----
  const borConflict = preview?.borConflict;
  const access = preview?.access;
  const customerUsers = access?.customerUsers ?? [];
  const pendingInviteCount = access?.pendingInviteCount ?? 0;
  const needsAccessDecision = customerUsers.length > 0;
  const canSubmit = (!borConflict?.hasConflict || borAcknowledged) && (!needsAccessDecision || keepAccess !== null);

  return (
    <Dialog
      open
      title='Reassign Customer'
      size='large'
      primaryBtnText='Reassign Customer'
      primaryBtnAction={handleSubmit}
      isPrimaryBtnDisabled={!canSubmit || isReassigning}
      isPrimaryBtnLoading={isReassigning}
      secondaryBtnText='Back'
      secondaryBtnAction={handleBack}
      handleCloseDialog={handleClose}
      sxProps={{ dialogContent: { px: 0 } }}
    >
      {/* Full-bleed header strip: DialogContent has px:0 (below), so the banner spans the modal's
          content width without negative margins (which would overflow). Squared, sits under title. */}
      <Alert severity='info' sx={{ mb: 2, borderRadius: 0 }}>
        The Named Insured will be updated to <strong>{preview?.targetCustomerName}</strong>.
      </Alert>
      <Box sx={{ display: 'flex', flexDirection: 'column', px: 3, mb: 2, gap: 1.5 }}>
        <Box>
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1, color: 'common.black' }}>
            Contact information
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2, fontWeight: 400 }}>
            The quote&apos;s current value is kept by default — select &quot;Target&quot; to update a field to the
            target customer&apos;s value instead.
          </Typography>
          <Table
            size='small'
            sx={{
              '& .MuiRadio-root:not(.Mui-checked) .MuiIconButton-root': {
                boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.3) !important',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '30%', color: 'common.black' }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '35%', color: 'common.black' }}>Current (keep)</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '35%', color: 'common.black' }}>Target (update)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview?.contactCompare.map((row) => {
                const choice = contactChoices[row.field] ?? 'keep';
                return (
                  // role=radiogroup + a shared field-derived `name` on both inputs groups the
                  // two options for assistive tech and enables arrow-key radio navigation.
                  <TableRow key={row.field} role='radiogroup' aria-label={row.label} sx={{ verticalAlign: 'middle' }}>
                    <TableCell sx={{ overflow: 'hidden' }}>
                      <Typography
                        variant='body2'
                        sx={{ fontWeight: 400, color: 'common.black', wordBreak: 'break-word' }}
                      >
                        {row.label}
                      </Typography>
                    </TableCell>
                    {row.matches ? (
                      <TableCell colSpan={2}>
                        <Typography variant='body2' sx={{ fontWeight: 400, color: 'text.secondary' }}>
                          {row.quoteCurrentValue
                            ? `${row.quoteCurrentValue} — values match, no change`
                            : 'Values match — no value on file'}
                        </Typography>
                      </TableCell>
                    ) : (
                      <>
                        <TableCell>
                          <FormControlLabel
                            control={
                              <RadioButton
                                name={`contact-${row.field}`}
                                value='keep'
                                checked={choice === 'keep'}
                                onChange={() => setChoice(row.field, 'keep')}
                                size='small'
                              />
                            }
                            label={
                              <Typography
                                variant='body2'
                                sx={{ fontWeight: 400, color: 'common.black', wordBreak: 'break-word' }}
                              >
                                {row.quoteCurrentValue ?? '—'}
                              </Typography>
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={
                              <RadioButton
                                name={`contact-${row.field}`}
                                value='update'
                                checked={choice === 'update'}
                                onChange={() => setChoice(row.field, 'update')}
                                size='small'
                              />
                            }
                            label={
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight: 400,
                                  color: row.targetValue == null ? 'text.secondary' : 'common.black',
                                  fontStyle: row.targetValue == null ? 'italic' : 'normal',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {row.targetValue ?? 'Clear value'}
                              </Typography>
                            }
                          />
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {errorMessage && (
          <Alert severity='error'>
            <AlertTitle>Reassignment failed</AlertTitle>
            {errorMessage}
          </Alert>
        )}
      </Box>

      {/* Full-bleed footer strips — span the same width as the top info banner (DialogContent px:0). */}
      {needsAccessDecision && (
        <Alert severity='warning' sx={{ mt: 1, borderRadius: 0 }}>
          <AlertTitle>Customer user access</AlertTitle>
          The following customer users currently have access to this quote. Should they keep access after it moves to{' '}
          <strong>{preview?.targetCustomerName}</strong>?
          <Box component='ul' sx={{ my: 1, pl: 3 }}>
            {customerUsers.map((user) => (
              <Typography component='li' key={user.userId} variant='body2' sx={{ wordBreak: 'break-word' }}>
                {user.name} ({user.email})
              </Typography>
            ))}
          </Box>
          <RadioGroup
            row
            aria-label='Keep customer user access after reassignment'
            name='keep-customer-user-access'
            value={keepAccess === null ? '' : keepAccess ? 'yes' : 'no'}
            onChange={(e) => setKeepAccess(e.target.value === 'yes')}
          >
            <FormControlLabel
              value='yes'
              control={<RadioButton size='small' color='warning' />}
              label={<Typography variant='body2'>Yes — keep access for all listed users</Typography>}
            />
            <FormControlLabel
              value='no'
              control={<RadioButton size='small' color='warning' />}
              label={<Typography variant='body2'>No — remove access for all</Typography>}
            />
          </RadioGroup>
        </Alert>
      )}

      {pendingInviteCount > 0 && (
        <Alert severity='info' sx={{ mt: 1, borderRadius: 0 }}>
          {pendingInviteCount} pending customer {pendingInviteCount === 1 ? 'invite' : 'invites'} will expire when the
          quote is moved and must be re-sent under the new customer.
        </Alert>
      )}

      {borConflict?.hasConflict && (
        <Alert severity='warning' sx={{ mt: 1, borderRadius: 0 }}>
          <AlertTitle>Broker-of-record conflict</AlertTitle>
          The target customer has a broker-of-record relationship with a different brokerage
          {borConflict.conflictingBrokerageName ? ` (${borConflict.conflictingBrokerageName})` : ''}.
          <FormControlLabel
            sx={{ mt: 1, ml: '-10px !important', display: 'flex' }}
            control={
              <Checkbox
                checked={borAcknowledged}
                onChange={(e) => setBorAcknowledged(e.target.checked)}
                color='warning'
              />
            }
            label='I acknowledge this conflict and want to proceed.'
          />
        </Alert>
      )}
    </Dialog>
  );
};
